import { useEffect, useState, type ReactNode } from 'react';
import { useMarketConfigs, useWithdrawalPayoutConfig } from '../../features/settings';
import type {
  WithdrawalPayoutFixedAmountDTO,
  WithdrawalPayoutProvider,
  WithdrawalPayoutProviderConfigDTO,
} from '../../features/settings/types/settings.types';
import { MarketCard } from './MarketConfigCard';
import { fenToYuan, formatDateTime } from '../../lib/format';

const MODE_OPTIONS = [
  { value: 'manual', label: '默认人工处理' },
  { value: 'auto', label: '默认自动打款' },
] as const;

type FixedAmountSelectorValue = 'none' | 'sypaymon' | 'alipay_direct' | `alipay_direct:${string}`;

const inputCls =
  'h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';

export function WithdrawalPayoutPage() {
  const [marketId, setMarketId] = useState('CN');
  return (
    <div className="space-y-5">
      <label className="flex items-center gap-3 text-sm font-medium text-gray-700">
        地区
        <select className={inputCls} style={{ width: 200 }} value={marketId} onChange={(event) => setMarketId(event.target.value)}>
          <option value="CN">中国 · 人民币</option>
          <option value="VN">越南 · 越南盾</option>
        </select>
      </label>
      {marketId === 'CN' ? <ChinaWithdrawalPayoutPage /> : <RegionalWithdrawalPayoutPage key={marketId} marketId={marketId} />}
    </div>
  );
}

function RegionalWithdrawalPayoutPage({ marketId }: { marketId: string }) {
  const { items, loading, savingMarketId, error, reload, save } = useMarketConfigs();
  if (loading) return <div className="py-10 text-center text-gray-500">加载地区配置中...</div>;
  if (error) return <div className="py-10 text-center text-red-500">{error}<button className="ml-3 text-primary" onClick={reload}>重试</button></div>;
  const item = items.find((market) => market.market_id === marketId);
  if (!item) return <div className="py-10 text-center text-gray-500">暂无该地区配置</div>;
  return <MarketCard key={item.market_id} mode="payout" item={item} saving={savingMarketId === marketId} onSave={save} />;
}

function ChinaWithdrawalPayoutPage() {
  const {
    data,
    form,
    loading,
    saving,
    error,
    allowUserIDsText,
    denyUserIDsText,
    parsedAllowUserIDs,
    parsedDenyUserIDs,
    credentialReady,
    warnings,
    updateField,
    updateProviderField,
    addAlipayDirectAccount,
    updateAlipayDirectAccount,
    removeAlipayDirectAccount,
    setDefaultMode,
    setChannelEnabled,
    toggleAutoChannel,
    addFixedAmount,
    updateFixedAmount,
    removeFixedAmount,
    setFixedAmountChannel,
    setAllowUserIDsText,
    setDenyUserIDsText,
    save,
    reload,
  } = useWithdrawalPayoutConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="mb-3 text-sm text-red-500">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <div className="space-y-2">
        <p className="text-sm text-gray-500">
          配置中国地区审核通过后的自动打款方式、支付通道和固定提现金额。
        </p>
        {!credentialReady && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            有已启用通道的接口凭据未配置完整，对应通道不会真正进入自动打款链路。
          </div>
        )}
        {warnings.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-700 space-y-1">
            {warnings.map((warning) => (
              <div key={warning}>• {warning}</div>
            ))}
          </div>
        )}
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-medium text-gray-900">运行策略</h2>
            <p className="mt-1 text-sm text-gray-500">控制审核通过后默认进入人工处理还是自动打款队列。</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Field label="提交后自动审核">
            <label className="flex h-10 items-center gap-3 rounded-md border border-gray-200 px-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.auto_review_enabled}
                onChange={(e) => updateField('auto_review_enabled', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{form.auto_review_enabled ? '开启' : '关闭'}</span>
            </label>
          </Field>
          <Field label="审核通过默认模式">
            <select
              value={form.default_mode}
              onChange={(e) => setDefaultMode(e.target.value as typeof form.default_mode)}
              className={inputCls}
            >
              {MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="自动打款金额上限（分）">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                value={form.max_auto_amount_fen}
                onChange={(e) => updateField('max_auto_amount_fen', Number(e.target.value) || 0)}
                className={inputCls}
              />
              <span className="shrink-0 text-xs text-gray-400">= ¥{fenToYuan(form.max_auto_amount_fen)}</span>
            </div>
          </Field>
          <Field label="查单间隔（秒）">
            <input
              type="number"
              min={0}
              value={form.query_interval_seconds}
              onChange={(e) => updateField('query_interval_seconds', Number(e.target.value) || 0)}
              className={inputCls}
            />
          </Field>
          <Field label="创建失败重试次数">
            <input
              type="number"
              min={0}
              value={form.retry_limit}
              onChange={(e) => updateField('retry_limit', Number(e.target.value) || 0)}
              className={inputCls}
            />
          </Field>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <ProviderPanel
          title="Sypaymon"
          description="三方支付宝出款通道。这里配置的固定金额只会以 sypaymon provider 提交。"
          provider="sypaymon"
          config={form.sypaymon}
          secretLabel="Secret"
          secretPlaceholder={data?.secret_configured ? '已配置，留空表示不修改' : '请输入 Sypaymon Secret'}
          onUpdate={updateProviderField}
        >
          <Field label="支付宝产品 ID">
            <input
              type="number"
              min={0}
              value={form.sypaymon.alipay_product_id}
              onChange={(e) => updateProviderField('sypaymon', 'alipay_product_id', Number(e.target.value) || 0)}
              className={inputCls}
            />
          </Field>
        </ProviderPanel>

        <ProviderPanel
          title="支付宝直连"
          description="官方商家转账到支付宝账户。这里配置的固定金额只会以 alipay_direct provider 提交。"
          provider="alipay_direct"
          config={form.alipay_direct_config}
          secretLabel="应用私钥"
          secretPlaceholder={data?.secret_configured ? '已配置，留空表示不修改' : '请输入支付宝应用私钥'}
          onUpdate={updateProviderField}
        >
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
            产品码：TRANS_ACCOUNT_NO_PWD
          </div>
          <Field label="应用公钥证书">
            <textarea
              value={form.alipay_direct_config.alipay_app_cert}
              onChange={(e) => updateProviderField('alipay_direct', 'alipay_app_cert', e.target.value)}
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              placeholder={data?.alipay_cert_configured ? '已配置，留空表示不修改' : '粘贴 appCertPublicKey.crt 内容'}
              autoComplete="off"
            />
          </Field>
          <Field label="支付宝公钥证书">
            <textarea
              value={form.alipay_direct_config.alipay_public_cert}
              onChange={(e) => updateProviderField('alipay_direct', 'alipay_public_cert', e.target.value)}
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              placeholder={data?.alipay_cert_configured ? '已配置，留空表示不修改' : '粘贴 alipayCertPublicKey_RSA2.crt 内容'}
              autoComplete="off"
            />
          </Field>
          <Field label="支付宝根证书">
            <textarea
              value={form.alipay_direct_config.alipay_root_cert}
              onChange={(e) => updateProviderField('alipay_direct', 'alipay_root_cert', e.target.value)}
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              placeholder={data?.alipay_cert_configured ? '已配置，留空表示不修改' : '粘贴 alipayRootCert.crt 内容'}
              autoComplete="off"
            />
          </Field>
          <Field label="支付宝公钥（普通公钥模式）">
            <textarea
              value={form.alipay_direct_config.alipay_public_key}
              onChange={(e) => updateProviderField('alipay_direct', 'alipay_public_key', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              placeholder={data?.alipay_public_key_configured ? '已配置，留空表示不修改' : '证书模式不用填'}
              autoComplete="off"
            />
          </Field>
        </ProviderPanel>
      </div>

      <AlipayDirectAccountsPanel
        accounts={form.alipay_direct_accounts}
        secretConfigured={Boolean(data?.secret_configured)}
        alipayPublicKeyConfigured={Boolean(data?.alipay_public_key_configured)}
        alipayCertConfigured={Boolean(data?.alipay_cert_configured)}
        onAdd={addAlipayDirectAccount}
        onUpdate={updateAlipayDirectAccount}
        onRemove={removeAlipayDirectAccount}
      />

      <UnifiedAmountTable
        amounts={form.fixed_amounts}
        sypaymonEnabled={form.sypaymon.enabled && form.alipay_enabled}
        alipayDirectEnabled={form.alipay_direct_config.enabled && form.alipay_enabled}
        alipayDirectAccounts={form.alipay_direct_accounts.filter((account) => account.enabled && (account.id ?? '').trim() !== '' && form.alipay_enabled)}
        onAdd={addFixedAmount}
        onUpdate={updateFixedAmount}
        onRemove={removeFixedAmount}
        onSetChannel={setFixedAmountChannel}
      />

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div>
          <h2 className="text-base font-medium text-gray-900">自动通道</h2>
          <p className="mt-1 text-sm text-gray-500">
            支付宝自动出款会根据提现单 provider 选择 Sypaymon 或直连；银行卡仍使用全局产品 ID。
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <ChannelCard
            title="支付宝"
            description="启用后，App 的支付宝固定金额会展示已启用 provider 的金额档位。"
            enabled={form.alipay_enabled}
            autoChecked={form.auto_channels.includes('alipay')}
            onToggleEnabled={(checked) => setChannelEnabled('alipay', checked)}
            onToggleAuto={(checked) => toggleAutoChannel('alipay', checked)}
            disabled={!form.alipay_enabled}
          >
            <div className="text-sm text-gray-600">
              Sypaymon 产品 ID：{form.sypaymon.alipay_product_id || '-'}；直连产品码：TRANS_ACCOUNT_NO_PWD
            </div>
          </ChannelCard>

          <ChannelCard
            title="银行卡"
            description="银行卡仍使用 Sypaymon 产品 ID，未配置前不要纳入自动打款。"
            enabled={form.bank_card_enabled}
            autoChecked={form.auto_channels.includes('bank_card')}
            onToggleEnabled={(checked) => setChannelEnabled('bank_card', checked)}
            onToggleAuto={(checked) => toggleAutoChannel('bank_card', checked)}
            disabled={!form.bank_card_enabled}
          >
            <Field label="银行卡产品 ID">
              <input
                type="number"
                min={0}
                value={form.bank_card_product_id}
                onChange={(e) => updateField('bank_card_product_id', Number(e.target.value) || 0)}
                className={inputCls}
              />
            </Field>
          </ChannelCard>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div>
          <h2 className="text-base font-medium text-gray-900">灰度名单与用户覆盖</h2>
          <p className="mt-1 text-sm text-gray-500">
            白名单和黑名单都支持换行、空格或逗号分隔的用户 ID。保存时会自动去重并升序归一。
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <Field label="自动打款白名单用户 ID">
            <textarea
              value={allowUserIDsText}
              onChange={(e) => setAllowUserIDsText(e.target.value)}
              rows={8}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              placeholder={'1001\n1002\n1003'}
            />
          </Field>
          <Field label="自动打款黑名单用户 ID">
            <textarea
              value={denyUserIDsText}
              onChange={(e) => setDenyUserIDsText(e.target.value)}
              rows={8}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              placeholder={'2001\n2002'}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <PreviewCard
            title="白名单预览"
            count={parsedAllowUserIDs.values.length}
            invalidCount={parsedAllowUserIDs.invalidTokens.length}
            values={parsedAllowUserIDs.values}
          />
          <PreviewCard
            title="黑名单预览"
            count={parsedDenyUserIDs.values.length}
            invalidCount={parsedDenyUserIDs.invalidTokens.length}
            values={parsedDenyUserIDs.values}
          />
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="text-sm text-gray-500 space-y-1">
          <div>最近更新时间：{data?.updated_at ? formatDateTime(data.updated_at) : '未保存到数据库'}</div>
          <div>最近更新人：{data?.updated_by ?? '-'}</div>
          <div>默认 webhook：{data?.notify_path || '-'}</div>
        </div>
        <button
          type="button"
          onClick={() => {
            void save();
          }}
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary/90 disabled:opacity-60"
        >
          {saving && <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />}
          保存并立即生效
        </button>
      </section>
    </div>
  );
}

function ProviderPanel(props: {
  title: string;
  description: string;
  provider: WithdrawalPayoutProvider;
  config: WithdrawalPayoutProviderConfigDTO;
  secretLabel: string;
  secretPlaceholder: string;
  onUpdate: (
    provider: WithdrawalPayoutProvider,
    key: keyof WithdrawalPayoutProviderConfigDTO,
    value: WithdrawalPayoutProviderConfigDTO[keyof WithdrawalPayoutProviderConfigDTO],
  ) => void;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-medium text-gray-900">{props.title}</h2>
          <p className="mt-1 text-sm text-gray-500">{props.description}</p>
        </div>
        <label className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={props.config.enabled}
            onChange={(e) => props.onUpdate(props.provider, 'enabled', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          启用
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="App ID">
          <input
            type="text"
            value={props.config.app_id}
            onChange={(e) => props.onUpdate(props.provider, 'app_id', e.target.value)}
            className={inputCls}
            placeholder="留空使用环境变量"
            autoComplete="off"
          />
        </Field>
        <Field label={props.secretLabel}>
          <input
            type="password"
            value={props.config.secret}
            onChange={(e) => props.onUpdate(props.provider, 'secret', e.target.value)}
            className={inputCls}
            placeholder={props.secretPlaceholder}
            autoComplete="new-password"
          />
        </Field>
        <Field label="Base URL">
          <input
            type="text"
            value={props.config.base_url}
            onChange={(e) => props.onUpdate(props.provider, 'base_url', e.target.value)}
            className={inputCls}
            placeholder="https://..."
            autoComplete="off"
          />
        </Field>
        <Field label="回调路径">
          <input
            type="text"
            value={props.config.notify_path}
            onChange={(e) => props.onUpdate(props.provider, 'notify_path', e.target.value)}
            className={inputCls}
          />
        </Field>
      </div>

      {props.children}
    </section>
  );
}

function AlipayDirectAccountsPanel(props: {
  accounts: WithdrawalPayoutProviderConfigDTO[];
  secretConfigured: boolean;
  alipayPublicKeyConfigured: boolean;
  alipayCertConfigured: boolean;
  onAdd: () => void;
  onUpdate: (
    index: number,
    key: keyof WithdrawalPayoutProviderConfigDTO,
    value: WithdrawalPayoutProviderConfigDTO[keyof WithdrawalPayoutProviderConfigDTO],
  ) => void;
  onRemove: (index: number) => void;
}) {
  const [closedAccountKeys, setClosedAccountKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const currentKeys = new Set(props.accounts.map(alipayAccountCollapseKey));
    setClosedAccountKeys((prev) => new Set([...prev].filter((key) => currentKeys.has(key))));
  }, [props.accounts]);

  const toggleAccountCollapsed = (key: string) => {
    setClosedAccountKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-medium text-gray-900">支付宝直连账号</h2>
          <p className="mt-1 text-sm text-gray-500">每个账号是一条独立通道，可在固定提现金额里单独选择。</p>
        </div>
        <button
          type="button"
          onClick={props.onAdd}
          className="shrink-0 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          添加账号
        </button>
      </div>
      {props.accounts.length === 0 && (
        <div className="rounded-md border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-400">
          暂无附加直连账号
        </div>
      )}
      <div className="space-y-4">
        {props.accounts.map((account, index) => (
          <div key={`${account.id ?? 'account'}-${index}`} className="rounded-lg border border-gray-200">
            {(() => {
              const accountKey = alipayAccountCollapseKey(account, index);
              const isOpen = !closedAccountKeys.has(accountKey);
              return (
                <>
                  <div className="flex items-center justify-between gap-3 rounded-t-lg border-b border-gray-100 bg-white px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-900">
                        {account.name || account.id || `支付宝账号${index + 1}`}
                      </div>
                      <label className="mt-1 inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={account.enabled}
                          onChange={(e) => props.onUpdate(index, 'enabled', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        启用账号
                      </label>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <button
                        type="button"
                        onClick={() => toggleAccountCollapsed(accountKey)}
                        className="text-sm text-gray-600 hover:underline"
                      >
                        {isOpen ? '收起' : '展开'}
                      </button>
                      <button
                        type="button"
                        onClick={() => props.onRemove(index)}
                        className="text-sm text-red-600 hover:underline"
                      >
                        删除
                      </button>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="max-h-[calc(100vh-220px)] overflow-y-auto px-4 pb-4 pt-4 space-y-4">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Field label="账号 ID">
                          <input
                            type="text"
                            value={account.id ?? ''}
                            onChange={(e) => props.onUpdate(index, 'id', e.target.value)}
                            className={inputCls}
                            placeholder="acct_a"
                            autoComplete="off"
                          />
                        </Field>
                        <Field label="账号名称">
                          <input
                            type="text"
                            value={account.name ?? ''}
                            onChange={(e) => props.onUpdate(index, 'name', e.target.value)}
                            className={inputCls}
                            placeholder="支付宝账号A"
                            autoComplete="off"
                          />
                        </Field>
                        <Field label="App ID">
                          <input
                            type="text"
                            value={account.app_id}
                            onChange={(e) => props.onUpdate(index, 'app_id', e.target.value)}
                            className={inputCls}
                            autoComplete="off"
                          />
                        </Field>
                        <Field label="应用私钥">
                          <input
                            type="password"
                            value={account.secret}
                            onChange={(e) => props.onUpdate(index, 'secret', e.target.value)}
                            className={inputCls}
                            placeholder={props.secretConfigured ? '已配置，留空表示不修改' : '请输入支付宝应用私钥'}
                            autoComplete="new-password"
                          />
                        </Field>
                        <Field label="Base URL">
                          <input
                            type="text"
                            value={account.base_url}
                            onChange={(e) => props.onUpdate(index, 'base_url', e.target.value)}
                            className={inputCls}
                            placeholder="留空使用默认直连配置"
                            autoComplete="off"
                          />
                        </Field>
                        <Field label="回调路径">
                          <input
                            type="text"
                            value={account.notify_path}
                            onChange={(e) => props.onUpdate(index, 'notify_path', e.target.value)}
                            className={inputCls}
                          />
                        </Field>
                      </div>
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        <Field label="支付宝公钥">
                          <textarea
                            value={account.alipay_public_key}
                            onChange={(e) => props.onUpdate(index, 'alipay_public_key', e.target.value)}
                            rows={3}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            placeholder={props.alipayPublicKeyConfigured ? '已配置，留空表示不修改' : '证书模式不用填'}
                            autoComplete="off"
                          />
                        </Field>
                        <Field label="应用公钥证书">
                          <textarea
                            value={account.alipay_app_cert}
                            onChange={(e) => props.onUpdate(index, 'alipay_app_cert', e.target.value)}
                            rows={3}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            placeholder={props.alipayCertConfigured ? '已配置，留空表示不修改' : '粘贴 appCertPublicKey.crt 内容'}
                            autoComplete="off"
                          />
                        </Field>
                        <Field label="支付宝公钥证书">
                          <textarea
                            value={account.alipay_public_cert}
                            onChange={(e) => props.onUpdate(index, 'alipay_public_cert', e.target.value)}
                            rows={3}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            placeholder={props.alipayCertConfigured ? '已配置，留空表示不修改' : '粘贴 alipayCertPublicKey_RSA2.crt 内容'}
                            autoComplete="off"
                          />
                        </Field>
                        <Field label="支付宝根证书">
                          <textarea
                            value={account.alipay_root_cert}
                            onChange={(e) => props.onUpdate(index, 'alipay_root_cert', e.target.value)}
                            rows={3}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                            placeholder={props.alipayCertConfigured ? '已配置，留空表示不修改' : '粘贴 alipayRootCert.crt 内容'}
                            autoComplete="off"
                          />
                        </Field>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ))}
      </div>
    </section>
  );
}

function alipayAccountCollapseKey(account: WithdrawalPayoutProviderConfigDTO, index: number): string {
  const id = (account.id ?? '').trim().toLowerCase();
  return id ? `id:${id}` : `index:${index}`;
}

function UnifiedAmountTable(props: {
  amounts: WithdrawalPayoutFixedAmountDTO[];
  sypaymonEnabled: boolean;
  alipayDirectEnabled: boolean;
  alipayDirectAccounts: WithdrawalPayoutProviderConfigDTO[];
  onAdd: () => void;
  onUpdate: <K extends keyof WithdrawalPayoutFixedAmountDTO>(
    index: number,
    key: K,
    value: WithdrawalPayoutFixedAmountDTO[K],
  ) => void;
  onRemove: (index: number) => void;
  onSetChannel: (index: number, value: FixedAmountSelectorValue) => void;
}) {
  const channelOptions = [
    { value: 'none', label: '不选择', disabled: false },
    { value: 'sypaymon', label: 'Sypaymon', disabled: !props.sypaymonEnabled },
    { value: 'alipay_direct', label: '支付宝直连（默认）', disabled: !props.alipayDirectEnabled },
    ...props.alipayDirectAccounts.map((account) => ({
      value: `alipay_direct:${(account.id ?? '').trim().toLowerCase()}`,
      label: account.name || account.id || '支付宝账号',
      disabled: false,
    })),
  ] as const;
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-medium text-gray-900">提现金额配置</h2>
          <p className="mt-1 text-sm text-gray-500">维护中国支付宝固定提现金额；越南金额请在页面顶部切换地区后配置。</p>
        </div>
        <button
          type="button"
          onClick={props.onAdd}
          className="shrink-0 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          添加金额
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500">
            <tr>
              <th className="px-3 py-2">启用</th>
              <th className="px-3 py-2">金额（分）</th>
              <th className="px-3 py-2">标签</th>
              <th className="px-3 py-2">排序</th>
              <th className="px-3 py-2">支付通道</th>
              <th className="px-3 py-2 text-right">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {props.amounts.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-400">
                  暂无金额档位
                </td>
              </tr>
            )}
            {props.amounts.map((amount, index) => (
              <tr key={`${amount.amount_fen}-${index}`}>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={amount.enabled}
                    onChange={(e) => props.onUpdate(index, 'enabled', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min={0}
                    value={amount.amount_fen}
                    onChange={(e) => props.onUpdate(index, 'amount_fen', Number(e.target.value) || 0)}
                    className={inputCls}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={amount.label}
                    onChange={(e) => props.onUpdate(index, 'label', e.target.value)}
                    className={inputCls}
                    placeholder="留空自动生成"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={amount.sort_order}
                    onChange={(e) => props.onUpdate(index, 'sort_order', Number(e.target.value) || 0)}
                    className={inputCls}
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    value={selectedAmountChannelValue(amount)}
                    onChange={(e) => props.onSetChannel(index, e.target.value as FixedAmountSelectorValue)}
                    className={inputCls}
                  >
                    {channelOptions.map((option) => (
                      <option key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => props.onRemove(index)}
                    className="text-sm text-red-600 hover:underline"
                  >
                    删除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function selectedAmountChannelValue(amount: WithdrawalPayoutFixedAmountDTO): FixedAmountSelectorValue {
  const channel = amount.channels.find((item) => item.channel === 'alipay' && item.enabled);
  if (!channel) return 'none';
  if (channel.provider !== 'sypaymon' && channel.provider !== 'alipay_direct') return 'none';
  const accountID = (channel.account_id ?? '').trim().toLowerCase();
  if (channel.provider === 'alipay_direct' && accountID) {
    return `alipay_direct:${accountID}`;
  }
  return channel.provider;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}

function ChannelCard(props: {
  title: string;
  description: string;
  enabled: boolean;
  autoChecked: boolean;
  disabled: boolean;
  onToggleEnabled: (checked: boolean) => void;
  onToggleAuto: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium text-gray-900">{props.title}</h3>
          <p className="mt-1 text-sm text-gray-500">{props.description}</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={props.enabled}
            onChange={(e) => props.onToggleEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          启用通道
        </label>
      </div>

      <label className={`inline-flex items-center gap-2 text-sm ${props.disabled ? 'text-gray-400' : 'text-gray-700'}`}>
        <input
          type="checkbox"
          checked={props.autoChecked}
          onChange={(e) => props.onToggleAuto(e.target.checked)}
          disabled={props.disabled}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
        />
        纳入自动打款 auto_channels
      </label>

      {props.children}
    </div>
  );
}

function PreviewCard(props: {
  title: string;
  count: number;
  invalidCount: number;
  values: number[];
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-gray-900">{props.title}</h3>
        <span className="text-xs text-gray-400">{props.count} 个有效 ID</span>
      </div>
      <div className="mt-2 text-xs text-gray-500">
        {props.invalidCount > 0 ? `存在 ${props.invalidCount} 个非法片段，保存时会被拦截。` : '未发现非法片段。'}
      </div>
      <div className="mt-3 max-h-28 overflow-auto rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-xs text-gray-700">
        {props.values.length > 0 ? props.values.join(', ') : '-'}
      </div>
    </div>
  );
}
